import { Clipboard } from '@angular/cdk/clipboard';
import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { QueryRef } from 'apollo-angular';

import { LazyLoadEvent, MenuItem } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import { Subject, debounceTime } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SubSink } from 'subsink';

import {
  BaseDynamicReportFragment,
  DynamicReportsGQL,
  DynamicReportsQuery,
  DynamicReportsQueryVariables,
  FolderInfo,
  UpdateDynamicReportGQL,
  ZoneDir,
} from '../../../generated/graphql.generated';
import { DetailsHelperService } from '../../services/details-helper.service';
import { FreyaMutateService } from '../../services/freya-mutate.service';
import { FreyaNotificationsService } from '../../services/freya-notifications.service';
import { WatchQueryHelper } from '../../utilities/watchQueryHelper';

/** Virtual folder name used to surface the static reports table in ReportTypesComponent. */
const STATIC_FOLDER_PATH = '/Static';

@Component({
  selector: 'app-dynamic-reports',
  templateUrl: './dynamic-reports.component.html',
  styleUrl: './dynamic-reports.component.scss'
})
export class DynamicReportsComponent implements OnInit, OnDestroy {

  @ViewChild('cm') contextMenu: ContextMenu;

  /** Requirement: Feature 2 (Issue #5821) — Emit the active path so the parent
   * (ReportTypesComponent) can show the static-reports table when at /Static. */
  @Output() pathChanged = new EventEmitter<string>();

  /** Requirement: Shared search box — emit search text so parent can pass it
   * to the static reports query when the user is browsing /Static. */
  @Output() searchChanged = new EventEmitter<string>();

  dynamicReportsQH: WatchQueryHelper = {
    skip: 0,
    limit: 10,
    loading: true,
    search: '',
  };

  dynamicReportsQueryRef: QueryRef<DynamicReportsQuery, DynamicReportsQueryVariables>;

  dynamicReports: BaseDynamicReportFragment[] = [];

  // Requirement: Feature 2 (Issue #5821) — Path/folder browsing state.
  folders: FolderInfo[] = [];
  currentPath = '/';

  /** All distinct folder paths seen in query responses — fed into the path autocomplete
   * in MutateDynamicReportComponent via additionalValues[existingPaths].
   * Only populated from backend `folders` data (not user-typed values), so it starts
   * with just '/' and grows as the user browses different folders. */
  allKnownPaths: string[] = ['/'];

  // Requirement: Right-click context menu for dynamic report rows.
  selectedReport: BaseDynamicReportFragment | null = null;
  contextMenuItems: MenuItem[] = [];

  subs = new SubSink();

  filterUpdated = new Subject<void>();

  constructor(
    private dynamicReportsGQL: DynamicReportsGQL,
    private updateDynamicReportGQL: UpdateDynamicReportGQL,
    private freyaMutate: FreyaMutateService,
    private notify: FreyaNotificationsService,
    private detailsHelper: DetailsHelperService,
    private clipboard: Clipboard,
    private router: Router,
  ) {}

  ngOnInit(): void {
    // Requirement: Folder-based routing — read the active folder path from the URL
    // path segments (/reports/<segments>) rather than a query param.
    // This makes folder URLs bookmarkable, shareable, and browser-history-friendly
    // without polluting the query string.
    const initialPath = this.parseFolderPathFromUrl(this.router.url);
    this.currentPath = initialPath;
    this.pathChanged.emit(this.currentPath);

    // Listen for NavigationEnd events so that browser back/forward and programmatic
    // router.navigate() calls both update the displayed folder without a full reload.
    this.subs.sink = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
    ).subscribe((e) => {
      const newPath = this.parseFolderPathFromUrl(e.urlAfterRedirects);
      if (newPath !== this.currentPath) {
        this.currentPath = newPath;
        this.pathChanged.emit(this.currentPath);
        this.dynamicReportsQH.skip = 0;
        this.retrieveDynamicReports();
      }
    });

    this.subs.sink = this.filterUpdated
      .pipe(debounceTime(800)).subscribe(() => {
        // Requirement: Shared search — emit current search text to parent so it can
        // apply to the static reports query when browsing /Static.
        this.searchChanged.emit(this.dynamicReportsQH.search);
        this.dynamicReportsQH.skip = 0;
        this.retrieveDynamicReports();
      });

    this.retrieveDynamicReports();

    // Requirement: Reload the list whenever a DynamicReport is created or updated
    // (e.g. after the user edits name/path/description via the context-menu Edit dialog)
    // so the table reflects the latest data without a manual page refresh.
    this.subs.sink = this.detailsHelper.getObjectUpdates('DynamicReport').subscribe((val) => {
      if (val.action === 'update' || val.action === 'create') {
        this.retrieveDynamicReports();
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  /**
   * Requirement: Feature 2 — Breadcrumb segments computed from the current path.
   * Always starts with a root "Reports" segment.
   */
  get breadcrumbs(): Array<{ label: string; path: string }> {
    const crumbs: Array<{ label: string; path: string }> = [{ label: 'Reports', path: '/' }];
    if (this.currentPath === '/') { return crumbs; }
    const segments = this.currentPath.split('/').filter(Boolean);
    let built = '';
    for (const seg of segments) {
      built += '/' + seg;
      crumbs.push({ label: seg, path: built });
    }
    return crumbs;
  }

  /** The virtual /Static folder card is only shown at the root level. */
  get showStaticFolder(): boolean {
    return this.currentPath === '/';
  }

  /**
   * Requirement: Folder-based routing — parse the folder path from a URL of the form
   * /reports/<segments>. Returns '/' for the root (/reports) URL.
   */
  private parseFolderPathFromUrl(url: string): string {
    // Requirement: Match both /reports (root) and /reports/<path>.
    // Excludes /reports/dashboards and /reports/dynamic which are separate routes.
    const match = url.match(/^\/reports(?:\/((?!dashboards|dynamic)[^?#]*))?/);
    if (!match) { return '/'; }
    let raw = (match[1] || '').replace(/\/$/, '');
    // Requirement: Decode up to twice to handle both single-encoded (%20 → space) and
    // double-encoded (%2520 → %20 → space) URLs. Double-encoding occurs when the backend
    // returns a path that already contains %20, and Angular's router encodes the % again.
    try { raw = decodeURIComponent(raw); } catch { /* keep as-is */ }
    try { raw = decodeURIComponent(raw); } catch { /* keep as-is */ }
    return raw ? '/' + raw : '/';
  }

  /** Fully decode a path string from the backend before passing it to Angular's router.
   * Backend-stored paths may contain URL-encoded characters (e.g. %20 for a space).
   * Angular would otherwise double-encode the % to %25, producing %2520 in the URL. */
  private decodeFolderPath(path: string): string {
    try {
      const once = decodeURIComponent(path);
      // Only apply a second decode if the first produced more encoded sequences
      return decodeURIComponent(once);
    } catch {
      try { return decodeURIComponent(path); } catch { return path; }
    }
  }

  navigateToFolder(path: string) {
    this.navigateToDirectoryPath(path);
  }

  navigateToBreadcrumb(path: string) {
    if (path === this.currentPath) { return; }
    this.navigateToDirectoryPath(path);
  }

  private navigateToDirectoryPath(path: string) {
    // Requirement: Decode the backend path before splitting into URL segments to prevent
    // double-encoding. Always navigate to /reports/... so the UrlMatcher route
    // is always matched. The empty segments case (/reports) maps to root '/'.
    const decoded = this.decodeFolderPath(path);
    const segments = decoded === '/' ? [] : decoded.split('/').filter(Boolean);
    this.router.navigate(['/reports', ...segments]);
  }

  retrieveMoreReports(event: LazyLoadEvent) {
    this.dynamicReportsQH.skip = event.first;
    this.dynamicReportsQH.limit = event.rows;
    this.retrieveDynamicReports();
  }

  retrieveDynamicReports() {
    // Requirement: Feature 2 — /Static is a virtual folder with no DynamicReport nodes.
    // Skip the API call entirely and clear local state so the table shows empty, letting
    // the parent (ReportTypesComponent) render the legacy static-reports table instead.
    if (this.currentPath === STATIC_FOLDER_PATH) {
      this.dynamicReports = [];
      this.folders = [];
      this.dynamicReportsQH.loading = false;
      return;
    }

    const filter = {
      zoneDir: ZoneDir.Any,
      search: this.dynamicReportsQH.search,
      // Requirement: When a search term is active, omit the path filter so results
      // come from ALL subfolders (not just the current folder). When browsing without
      // a search term, restrict to the current folder so the table and folder cards
      // only show content at this level of the hierarchy.
      path: this.dynamicReportsQH.search ? undefined : this.currentPath,
    };

    if (this.dynamicReportsQueryRef) {
      this.dynamicReportsQueryRef.refetch({
        skip: this.dynamicReportsQH.skip,
        limit: this.dynamicReportsQH.limit,
        filter,
      });
      return;
    }

    this.dynamicReportsQueryRef = this.dynamicReportsGQL.watch({
      skip: this.dynamicReportsQH.skip,
      limit: this.dynamicReportsQH.limit,
      filter,
    }, {
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    });

    this.subs.sink = this.dynamicReportsQueryRef.valueChanges.subscribe((res) => {
      this.dynamicReportsQH.loading = res.loading;

      if (res.loading) { return; }

      this.dynamicReportsQH.total = res.data.dynamicReports.total;
      this.dynamicReports = res.data.dynamicReports.reports;
      this.folders = res.data.dynamicReports.folders ?? [];

      // Requirement: Feature 2 — Accumulate all known folder paths so the mutate
      // dialog's path autocomplete can suggest existing locations.
      this.accumulatePaths(this.folders);
    });
  }

  /**
   * Build up the `allKnownPaths` array from folder data returned by the API.
   * Each folder path and all its ancestor segments are added so autocomplete
   * works for both leaf and intermediate paths.
   */
  private accumulatePaths(folders: FolderInfo[]) {
    const pathSet = new Set(this.allKnownPaths);
    for (const folder of folders) {
      const segments = folder.path.split('/').filter(Boolean);
      let built = '';
      for (const seg of segments) {
        built += '/' + seg;
        pathSet.add(built);
      }
    }
    this.allKnownPaths = Array.from(pathSet).sort();
  }

  clearSearch() {
    this.dynamicReportsQH.search = '';
    this.dynamicReportsQH.skip = 0;
    this.searchChanged.emit('');
    this.retrieveDynamicReports();
  }

  // ---------------------------------------------------------------------------
  // Requirement: Right-click context menu on dynamic report rows
  // ---------------------------------------------------------------------------

  /**
   * Called on (contextmenu) on each report row. Sets the selected report
   * and builds context menu items before showing the menu.
   */
  onRightClick(event: MouseEvent, report: BaseDynamicReportFragment) {
    this.selectedReport = report;
    this.contextMenuItems = this.buildContextMenu(report);
    // Let PrimeNG contextmenu show on the next tick after items are set
    setTimeout(() => this.contextMenu?.show(event), 0);
    event.preventDefault();
  }

  private buildContextMenu(report: BaseDynamicReportFragment): MenuItem[] {
    // Requirement: "move to folder" submenu is built from all known paths,
    // excluding the report's current path (no-op move).
    const moveFolderItems: MenuItem[] = this.allKnownPaths
      .filter((p) => p !== (report.path || '/'))
      .map((p) => ({
        label: p === '/' ? '/ (root)' : p,
        icon: 'pi pi-folder',
        command: () => this.moveReportToFolder(report, p),
      }));

    return [
      {
        label: 'Duplicate',
        icon: 'pi pi-copy',
        // Requirement: Duplicate navigates to the new-report page with the report's
        // parameters pre-filled, as if no report has been saved yet.
        command: () => this.router.navigate(['/reports', 'dynamic', report.id]),
      },
      {
        label: 'Move to Folder',
        icon: 'pi pi-folder-open',
        items: moveFolderItems.length ? moveFolderItems : [{ label: 'No other folders', disabled: true }],
      },
      {
        label: 'Edit',
        icon: 'pi pi-pencil',
        command: () => this.openEditDialog(report),
      },
      {
        label: 'Copy Link',
        icon: 'pi pi-link',
        command: () => {
          const url = `${window.location.origin}/reports/dynamic/${report.id}`;
          this.clipboard.copy(url);
          this.notify.success('Link copied to clipboard');
        },
      },
    ];
  }

  private openEditDialog(report: BaseDynamicReportFragment) {
    this.freyaMutate.openMutateObject({
      mutateType: 'update',
      objectType: 'dynamicReport',
      additionalValues: [
        { property: 'dynamicReport', value: report },
        { property: 'existingPaths', value: this.allKnownPaths },
      ],
    });
  }

  private moveReportToFolder(report: BaseDynamicReportFragment, targetPath: string) {
    this.updateDynamicReportGQL.mutate({
      reportId: report.id,
      path: targetPath,
    }).subscribe({
      next: () => {
        this.notify.success('Report moved', `"${report.name}" moved to ${targetPath}`);
        this.detailsHelper.pushUpdate({
          id: report.id,
          action: 'update',
          type: 'DynamicReport',
        });
        this.retrieveDynamicReports();
      },
      error: (err) => {
        console.error('Failed to move report:', err);
        this.notify.error('Failed to move report', 'An error occurred, please try again');
      },
    });
  }
}
